import { C, R, T } from '../theme';
import { Icon } from '../icons';
import type { SetupStatus, SetupItem } from '../api';

/**
 * Finish setting up — the gaps on the owner's own record, each naming the
 * feature it switches on.
 *
 * Premise ships a tenant portal, a WhatsApp reminder and a magic-link invite
 * that all silently do nothing when a phone or email is missing. Previously the
 * only sign of that was a Nudge button at 45% opacity. This says which field is
 * missing and what filling it unlocks, and links straight to the sheet that
 * fills it. It disappears once the list is done.
 */
export function SetupCard({
  setup,
  onGo,
}: {
  setup?: SetupStatus;
  /** Open a tenancy, optionally straight into one of its sheets. */
  onGo: (tenancyId: string, sheet?: SetupItem['action']) => void;
}) {
  if (!setup || setup.complete) return null;

  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.line}`,
        borderRadius: R.lg,
        overflow: 'hidden',
        marginBottom: 18,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '12px 15px',
          background: C.soft,
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        <span style={{ fontWeight: 650, fontSize: 14, color: C.ink }}>Finish setting up</span>
        <span style={{ fontSize: T.label, color: C.accent2, fontWeight: 600 }}>
          {setup.done} of {setup.total} done
        </span>
      </div>

      {setup.items.map((item, i) => (
        <div
          key={item.key}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 11,
            padding: '11px 15px',
            borderTop: i === 0 ? undefined : `1px solid ${C.line2}`,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 17,
              height: 17,
              borderRadius: 5,
              flex: 'none',
              marginTop: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: item.done ? C.green : C.card,
              border: `1.5px solid ${item.done ? C.green : C.line}`,
            }}
          >
            {item.done && <Icon n="check" size={11} color="#fff" />}
          </span>

          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: 'block',
                fontSize: T.body,
                color: item.done ? C.muted : C.ink,
                textDecoration: item.done ? 'line-through' : undefined,
                lineHeight: 1.45,
              }}
            >
              {item.label}
            </span>
            {!item.done && (
              <span style={{ display: 'block', fontSize: 11.8, color: C.muted, marginTop: 2 }}>
                {item.why}
              </span>
            )}
          </span>

          {!item.done && item.tenancyId && (
            <button
              onClick={() => onGo(item.tenancyId as string, item.action)}
              style={{
                border: 0,
                background: 'transparent',
                color: C.accent,
                fontWeight: 600,
                fontSize: 11.5,
                cursor: 'pointer',
                padding: '2px 0 0',
                whiteSpace: 'nowrap',
                flex: 'none',
              }}
            >
              {item.action === 'payment' ? 'Record' : 'Add'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
